import { Alert, Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { LIFELINE_REMOVAL_REASONS } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { ApiError } from '../../lib/api';
import { formatCents } from '../../lib/format';
import { useFeatureReleased } from '../../release';
import { useRemoveLifeline } from './api';

/** Release switch of "Remove Lifeline". */
export const REMOVE_LIFELINE_FEATURE = 'lifeline-remove';
const LIFELINE_TYPES = ['LifeLine', 'Combo Plan (Lifeline + ACP)'];

/**
 * "Remove Lifeline" on Change Account Type (the owner's rule of 2026-10-28): the line stays in
 * service and moves to the cheapest prepaid plan of its carrier in its state.
 */
export function RemoveLifelineSection({
  customerId,
  accountType,
}: {
  customerId: string;
  accountType: string;
}) {
  const notify = useNotify();
  const featureOn = useFeatureReleased();
  const [reason, setReason] = useState('');
  const remove = useRemoveLifeline(customerId);
  if (!featureOn(REMOVE_LIFELINE_FEATURE) || !LIFELINE_TYPES.includes(accountType)) return null;
  const problem =
    remove.error instanceof ApiError
      ? remove.error.details.find((d) => d.path === 'reason')?.message
      : undefined;
  return (
    <Box component="section" aria-label="Remove Lifeline" sx={{ mb: 3 }}>
      <Typography variant="h3" sx={{ mb: 1 }}>
        Remove Lifeline
      </Typography>
      <Typography sx={{ mb: 1.5 }}>
        This account is on Lifeline. When the customer loses Lifeline, the line stays in service and
        moves at once to the lowest-priced prepaid plan of its carrier in its state; the customer
        then pays for it.
      </Typography>
      {remove.data ? (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          Lifeline removed. The line is now on {remove.data.planName} (
          {formatCents(remove.data.priceCents)}).
        </Alert>
      ) : null}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <TextField
          select
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ minWidth: 300 }}
          error={Boolean(problem)}
          helperText={problem}
        >
          {LIFELINE_REMOVAL_REASONS.map((r) => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          color="warning"
          disabled={remove.isPending}
          onClick={() =>
            void remove
              .mutateAsync(reason)
              .then(() => notify.success('Lifeline removed.'))
              .catch((err) => (problem ? undefined : notify.error(err)))
          }
        >
          Remove Lifeline
        </Button>
      </Box>
    </Box>
  );
}
